import { toEnumValue } from '$lib/shared/enums';
import { CurrencyCode } from './intl';
import { getLocale, LocaleId as LocaleEnum, newIntlLocale, type LocaleId } from './locales';

/**
 * Get the currency symbol for a given locale or currency code.
 * Uses Intl.NumberFormat.formatToParts() for reliable multi-character symbol support (e.g., A$, kr, R$).
 */
export function getCurrencySymbol(localeIdOrCurrencyCode?: LocaleId | CurrencyCode): string {
  // If a CurrencyCode was passed directly, format with a default locale
  const currencyCode = toEnumValue(CurrencyCode, localeIdOrCurrencyCode as string);
  if (currencyCode) {
    return extractSymbol('en', currencyCode);
  }

  // Otherwise treat as LocaleId
  const localeId =
    (localeIdOrCurrencyCode as LocaleId) ??
    toEnumValue(LocaleEnum, navigator.language, { ignoreCase: true }) ??
    LocaleEnum.EnglishUnitedStates;
  const locale = getLocale(localeId);
  const intlLocale = newIntlLocale(localeId);
  return extractSymbol(intlLocale, locale.currencyCode);
}

function extractSymbol(locale: string | Intl.Locale, currencyCode: string): string {
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    currencyDisplay: 'narrowSymbol',
  });
  const parts = formatter.formatToParts(0);
  const currencyPart = parts.find((p) => p.type === 'currency');
  return currencyPart?.value ?? currencyCode;
}
